import { useCallback, useEffect, useRef } from "react";
import { useUserProfile } from "@/contexts/UserProfileContext";
import {
  isDocumentTitlePlaceholder,
  suggestDocumentTitleFromBody,
} from "@/lib/documentTitleSuggestion";
import {
  applyDocumentNamingPreferences,
  getDocumentNamingPreferencesFromProfile,
} from "@/lib/documentNamingPreferences";

export interface UseDebouncedDocumentTitleSuggestionArgs {
  /** Rich-text HTML or plain string from the editor */
  contentHtml: string;
  setTitle: (value: string | ((prev: string) => string)) => void;
  /** When false, timers are cleared and no suggestions run */
  enabled: boolean;
  /** Optional stable topic prefix to use for chat-scoped docs */
  topicTitle?: string | null;
  createdAt?: Date | string | number | null;
  debounceMs?: number;
}

/**
 * Debounced title suggestion from body before save. Call `onUserEditedTitle` from the title input's onChange
 * before updating title state so manual edits are not overwritten.
 */
export function useDebouncedDocumentTitleSuggestion({
  contentHtml,
  setTitle,
  enabled,
  topicTitle,
  createdAt,
  debounceMs = 1000,
}: UseDebouncedDocumentTitleSuggestionArgs) {
  const { profile } = useUserProfile();
  const namingPreferences = getDocumentNamingPreferencesFromProfile(profile);
  const titleRef = useRef("");
  const userLockedRef = useRef(false);
  const seqRef = useRef(0);

  const syncTitleRef = useCallback((t: string) => {
    titleRef.current = t;
  }, []);

  const onUserEditedTitle = useCallback(() => {
    userLockedRef.current = true;
  }, []);

  const resetSuggestionState = useCallback(() => {
    userLockedRef.current = false;
    seqRef.current += 1;
  }, []);

  useEffect(() => {
    if (!enabled) {
      seqRef.current += 1;
      return;
    }
    if (userLockedRef.current) return;

    const timer = window.setTimeout(() => {
      const runSeq = ++seqRef.current;
      void (async () => {
        try {
          const currentTitle = titleRef.current;
          let suggested = "";

          if (isDocumentTitlePlaceholder(currentTitle)) {
            suggested = await suggestDocumentTitleFromBody(contentHtml, {
              topicTitle,
              createdAt,
              namingPreferences,
            });
          } else {
            suggested = applyDocumentNamingPreferences(currentTitle, {
              topicTitle,
              createdAt,
              preferences: namingPreferences,
            });
          }

          if (runSeq !== seqRef.current) return;
          if (userLockedRef.current) return;
          if (!suggested) return;
          setTitle((prev) => {
            if (userLockedRef.current) return prev;
            if (!isDocumentTitlePlaceholder(prev)) {
              const reformatted = applyDocumentNamingPreferences(prev, {
                topicTitle,
                createdAt,
                preferences: namingPreferences,
              });
              return reformatted === prev ? prev : reformatted;
            }
            return suggested;
          });
        } catch (e) {
          console.warn("Document title suggestion failed:", e);
        }
      })();
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [contentHtml, enabled, topicTitle, createdAt, debounceMs, setTitle, namingPreferences]);

  return {
    syncTitleRef,
    onUserEditedTitle,
    resetSuggestionState,
  };
}
